import { schemas } from '@repo/contracts'
import { z } from 'zod'

export const conversationSafePositiveIntegerSchema = z.number().int().positive().max(Number.MAX_SAFE_INTEGER)
export const conversationSafeIntegerSchema = z.number().int().min(Number.MIN_SAFE_INTEGER).max(Number.MAX_SAFE_INTEGER)
export const conversationSlotKeySchema = z
  .string()
  .min(1)
  .max(128)
  .refine((value) => value.trim() === value)

export const conversationByteBoundedText = (maximum: number) =>
  z
    .string()
    .min(1)
    .refine((value) => value.trim() !== '' && new TextEncoder().encode(value).byteLength <= maximum)

// The brief-value union is the one family used in both directions: corrections send it, and
// snapshots and frames return it. So it is built twice, from one set of value tightenings, and only
// the catchall differs. `postprocess.mjs` writes the union with `additionalProperties: false`, so
// every member arrives `.strict()` from the generated package; the last catchall call wins, which
// is why the response variant can override it here without regenerating the contract. See ADR-176.
const documentValueShape = {
  docId: conversationSafePositiveIntegerSchema,
  // Pasted text has no file name; source identity and revision remain required.
  fileName: z.string().refine((value) => value.trim() === value),
  updatedAtTsMs: conversationSafePositiveIntegerSchema
}
const artifactObservationShape = {
  proposalUpdatedAtTsMs: conversationSafePositiveIntegerSchema,
  sectionUpdatedAtTsMs: conversationSafePositiveIntegerSchema
}
const uniqueDocuments = <T extends z.ZodType<{ docId: number }>>(document: T) =>
  z
    .array(document)
    .min(1)
    .max(32)
    .refine((documents) => new Set(documents.map((entry) => entry.docId)).size === documents.length)

export const conversationBriefDocumentValueSchema = schemas.apihttp_ConversationBriefDocumentValue
  .extend(documentValueShape)
  .strict()

const nonnegativeSafeInteger = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER)

// A bound source-proposal slot carries a whole-proposal reference whose section
// identity is zero, while a draft-section slot keeps a positive one; the type says
// which. Writers older than M9.23 sent sections only and no type, so an absent type
// defaults to the shape those writers meant rather than failing every stored frame
// on replay (ADR-176).
function refineArtifactReferenceIdentity(
  reference: { type: 'proposal' | 'proposal-section'; sectionId: number },
  context: z.RefinementCtx
): void {
  if (reference.type === 'proposal') {
    if (reference.sectionId !== 0) {
      context.addIssue({ code: 'custom', message: 'a whole-proposal reference names no section' })
    }
    return
  }
  if (reference.sectionId <= 0) {
    context.addIssue({ code: 'custom', message: 'a proposal-section reference needs its section' })
  }
}

const artifactReferenceIdentityShape = {
  type: z.enum(['proposal-section', 'proposal']).default('proposal-section'),
  proposalId: conversationSafePositiveIntegerSchema,
  sectionId: nonnegativeSafeInteger
}

export const conversationBriefArtifactObservationSchema = schemas.apihttp_ConversationProposalSectionObservationValue
  .extend(artifactObservationShape)
  .strict()

export const conversationBriefArtifactReferenceSchema = schemas.apihttp_ConversationArtifactReferenceValue
  .extend({
    ...artifactReferenceIdentityShape,
    observation: conversationBriefArtifactObservationSchema
  })
  .strict()
  .superRefine(refineArtifactReferenceIdentity)

// Sent, so exact: an unknown key in a correction we build ourselves is our own bug.
export const conversationBriefValueSchema = z.discriminatedUnion('type', [
  schemas.apihttp_ConversationTextBriefValue.extend({ text: conversationByteBoundedText(32 * 1024) }).strict(),
  schemas.apihttp_ConversationIntegerBriefValue.extend({ integer: conversationSafeIntegerSchema }).strict(),
  schemas.apihttp_ConversationDocumentBriefValue.extend({ document: conversationBriefDocumentValueSchema }).strict(),
  schemas.apihttp_ConversationDocumentsBriefValue
    .extend({ documents: uniqueDocuments(conversationBriefDocumentValueSchema) })
    .strict(),
  schemas.apihttp_ConversationArtifactBriefValue.extend({ artifact: conversationBriefArtifactReferenceSchema }).strict()
])

const briefDocumentValueResponseSchema = schemas.apihttp_ConversationBriefDocumentValue
  .extend(documentValueShape)
  .strip()
const briefArtifactObservationResponseSchema = schemas.apihttp_ConversationProposalSectionObservationValue
  .extend(artifactObservationShape)
  .strip()
const briefArtifactReferenceResponseSchema = schemas.apihttp_ConversationArtifactReferenceValue
  .extend({
    ...artifactReferenceIdentityShape,
    observation: briefArtifactObservationResponseSchema
  })
  .strip()
  .superRefine(refineArtifactReferenceIdentity)

// Returned, so tolerant: a server that describes a slot value more fully than this bundle can
// render must cost the reader that one field, not the conversation. M9.16 adds connector name,
// last sync and locator to the document value, and this is what lets it deploy without breaking
// every tab opened before it.
export const conversationBriefValueResponseSchema = z.discriminatedUnion('type', [
  schemas.apihttp_ConversationTextBriefValue.extend({ text: conversationByteBoundedText(32 * 1024) }).strip(),
  schemas.apihttp_ConversationIntegerBriefValue.extend({ integer: conversationSafeIntegerSchema }).strip(),
  schemas.apihttp_ConversationDocumentBriefValue.extend({ document: briefDocumentValueResponseSchema }).strip(),
  schemas.apihttp_ConversationDocumentsBriefValue
    .extend({ documents: uniqueDocuments(briefDocumentValueResponseSchema) })
    .strip(),
  schemas.apihttp_ConversationArtifactBriefValue.extend({ artifact: briefArtifactReferenceResponseSchema }).strip()
])

// Response-only (brief slots in snapshots and frames).
export const conversationBriefSourceSchema = schemas.apihttp_ConversationBriefSource
  .extend({
    messageId: conversationSafePositiveIntegerSchema,
    updatedAtTsMs: conversationSafePositiveIntegerSchema
  })
  .strip()

const conversationUTF8Length = (value: string) => new TextEncoder().encode(value).byteLength

function rejectDuplicateConversationJSONKeys(source: string): void {
  let index = 0
  const skipWhitespace = () => {
    while (/\s/.test(source[index] ?? '')) index += 1
  }
  const readString = (): string => {
    const start = index
    if (source[index] !== '"') throw new Error('expected JSON string')
    index += 1
    while (index < source.length) {
      if (source[index] === '\\') {
        index += 2
        continue
      }
      if (source[index] === '"') {
        index += 1
        return JSON.parse(source.slice(start, index)) as string
      }
      index += 1
    }
    throw new Error('unterminated JSON string')
  }
  const readValue = (): void => {
    skipWhitespace()
    const token = source[index]
    if (token === '{') {
      index += 1
      skipWhitespace()
      const keys = new Set<string>()
      if (source[index] === '}') {
        index += 1
        return
      }
      while (index < source.length) {
        skipWhitespace()
        const key = readString()
        if (keys.has(key)) throw new Error(`duplicate JSON field ${key}`)
        keys.add(key)
        skipWhitespace()
        if (source[index] !== ':') throw new Error('expected JSON colon')
        index += 1
        readValue()
        skipWhitespace()
        if (source[index] === '}') {
          index += 1
          return
        }
        if (source[index] !== ',') throw new Error('expected JSON object separator')
        index += 1
      }
      throw new Error('unterminated JSON object')
    }
    if (token === '[') {
      index += 1
      skipWhitespace()
      if (source[index] === ']') {
        index += 1
        return
      }
      while (index < source.length) {
        readValue()
        skipWhitespace()
        if (source[index] === ']') {
          index += 1
          return
        }
        if (source[index] !== ',') throw new Error('expected JSON array separator')
        index += 1
      }
      throw new Error('unterminated JSON array')
    }
    if (token === '"') {
      readString()
      return
    }
    const start = index
    while (index < source.length && !/[\s,\]}]/.test(source[index] ?? '')) index += 1
    if (start === index) throw new Error('expected JSON value')
    JSON.parse(source.slice(start, index))
  }

  readValue()
  skipWhitespace()
  if (index !== source.length) throw new Error('trailing JSON content')
}

export function parseCanonicalConversationJSON(source: string, maximumBytes: number): unknown {
  if (conversationUTF8Length(source) > maximumBytes) throw new Error('conversation JSON is too large')
  rejectDuplicateConversationJSONKeys(source)
  return JSON.parse(source) as unknown
}
